import React, { useEffect, useState } from "react";
import { Bell, Loader2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { fmtDate, useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Field } from "../components/fields";

export default function Settings() {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [logoNew, setLogoNew] = useState("");
  const [email, setEmail] = useState("");
  const [prefix, setPrefix] = useState("");
  const [prefs, setPrefs] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [allMods, setAllMods] = useState([]);
  const [allowedMods, setAllowedMods] = useState(null);
  const [disabledMods, setDisabledMods] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => { setName(r.data.tenant_name || ""); setEmail(r.data.alert_email || ""); setPrefix(r.data.id_prefix || ""); setPrefs(r.data.notify_prefs || null); setLogo(r.data.logo || ""); setAllMods(r.data.all_modules || []); setAllowedMods(r.data.allowed_modules ?? null); setDisabledMods(r.data.disabled_modules || []); }).catch(() => {});
    api.get("/settings/alerts-log").then((r) => setLog(r.data)).catch(() => {});
  }, []);

  if (user?.role !== "admin") {
    return <div className="ec-card p-10 text-center text-sm text-slate-500" data-testid="settings-forbidden">{t("u.only_admin")}</div>;
  }

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/settings", { name: name || null, alert_email: email || null, id_prefix: prefix || null, notify_prefs: prefs, logo: logoNew || null, disabled_modules: disabledMods });
      await refreshUser();
      if (logoNew) { setLogo(logoNew); setLogoNew(""); }
      refreshUser?.();
      toast.success(t("st.saved"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const TYPE_LABEL = { list_match: "Listas restrictivas", high_risk: "Riesgo alto", ros: "ROS", freeze: "Congelamiento CSNU" };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("st.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("st.subtitle")}</p>
      </div>

      <form onSubmit={save} className="ec-card p-6 space-y-4" data-testid="settings-form">
        <Field label={t("st.name")}>
          <input className="ec-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" />
          <p className="text-[11px] text-slate-400 mt-1">{t("st.name_help")}</p>
        </Field>
        <Field label={t("st.logo")}>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
              {(logoNew || logo) ? (
                <img src={logoNew || logo} alt="logo" className="max-h-12 max-w-12 object-contain" data-testid="settings-logo-preview" />
              ) : (
                <span className="text-[10px] text-slate-300 font-bold">LOGO</span>
              )}
            </div>
            <div className="space-y-1">
              <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="settings-logo-input" className="hidden" id="settings-logo-file"
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (!f) return;
                       if (f.size > 102400) { toast.error(t("st.logo_error")); e.target.value = ""; return; }
                       const rd = new FileReader();
                       rd.onload = () => setLogoNew(rd.result);
                       rd.readAsDataURL(f);
                     }} />
              <label htmlFor="settings-logo-file" data-testid="settings-logo-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C] cursor-pointer">
                {t("st.logo_change")}
              </label>
              <p className="text-[11px] text-slate-400">{t("st.logo_help")}</p>
            </div>
          </div>
        </Field>
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <Bell className="w-4 h-4 text-[#044CBC]" /> {t("st.alert_email")}
        </div>
        <p className="text-xs text-slate-500">{t("st.alert_email_help")}</p>
        <Field label={t("auth.email")}>
          <input className="ec-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cumplimiento@suempresa.com" data-testid="alert-email-input" />
        </Field>
        <Field label={t("st.id_prefix")}>
          <input className="ec-input uppercase" value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} placeholder="SOL" maxLength={8} data-testid="id-prefix-input" />
          <p className="text-[11px] text-slate-400 mt-1">{t("st.id_prefix_help")}</p>
        </Field>
        {prefs && (
          <div className="pt-3 border-t border-slate-100" data-testid="notify-matrix">
            <h3 className="font-heading font-bold text-slate-900 text-sm">{t("st.notify_matrix")}</h3>
            <p className="text-[11px] text-slate-400 mb-3">{t("st.notify_matrix_help")}</p>
            <div className="space-y-2">
              {Object.keys(prefs).map((k) => (
                <div key={k} className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-lg px-3 py-2.5" data-testid={`notify-row-${k}`}>
                  <label className="flex items-center gap-2 flex-1 min-w-[240px] cursor-pointer">
                    <input type="checkbox" checked={prefs[k].enabled} onChange={(e) => setPrefs({ ...prefs, [k]: { ...prefs[k], enabled: e.target.checked } })} data-testid={`notify-toggle-${k}`} />
                    <span className="text-xs font-semibold text-slate-700">{t(`st.nt_${k}`)}</span>
                  </label>
                  <input className="ec-input !py-1.5 text-xs sm:max-w-xs" type="email" placeholder={t("st.nt_email")} value={prefs[k].email} onChange={(e) => setPrefs({ ...prefs, [k]: { ...prefs[k], email: e.target.value } })} data-testid={`notify-email-${k}`} />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2.5">
          <ShieldCheck className="w-4 h-4 text-[#033A8C] shrink-0" /> {t("st.legal")}
        </div>
        {allMods.length > 0 && (
          <div className="space-y-3" data-testid="modules-section">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><ShieldCheck className="w-4 h-4 text-[#044CBC]" /> {t("st.modules")}</div>
            <p className="text-xs text-slate-500">{t("st.modules_help")}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {allMods.map((m) => {
                const alwaysOn = ["vessels", "pep_panama"].includes(m);
                const planOff = allowedMods !== null && !allowedMods.includes(m) && !["fbi", "interpol", "wb", "osint", "namus", "icmp", "cyber", "ntas", "vessels"].includes(m);
                const on = alwaysOn || (!planOff && !disabledMods.includes(m));
                return (
                  <label key={m} className={`flex items-center gap-2 rounded-lg px-3 py-2 border cursor-pointer ${planOff ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed" : on ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50"}`} data-testid={`module-row-${m}`}>
                    <input type="checkbox" disabled={planOff || alwaysOn} checked={on} onChange={(e) => setDisabledMods(e.target.checked ? disabledMods.filter((x) => x !== m) : [...disabledMods, m])} data-testid={`module-toggle-${m}`} />
                    <span className="text-xs font-semibold">{t(`nav.${m}`)}{planOff && <span className="ml-1 text-[10px] uppercase">({t("st.module_plan")})</span>}{alwaysOn && <span className="ml-1 text-[10px] uppercase text-emerald-700">({t("st.module_always")})</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button type="submit" disabled={busy} data-testid="settings-save" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("common.save")}
          </button>
        </div>
      </form>

      <div className="ec-card overflow-hidden" data-testid="alerts-log">
        <div className="px-5 py-3 border-b border-slate-100 font-heading font-bold text-slate-900 text-sm">{t("st.log")}</div>
        {log.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">{t("h.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-2 text-xs font-bold uppercase text-slate-500">{t("common.type")}</th>
                <th className="px-4 py-2 text-xs font-bold uppercase text-slate-500">{t("st.alert_email")}</th>
                <th className="px-4 py-2 text-xs font-bold uppercase text-slate-500">{t("common.result")}</th>
                <th className="px-4 py-2 text-xs font-bold uppercase text-slate-500">{t("common.date")}</th>
              </tr>
            </thead>
            <tbody>
              {log.map((a, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`alert-log-${i}`}>
                  <td className="px-4 py-2 text-xs font-semibold text-slate-700">{TYPE_LABEL[a.alert_type] || a.alert_type}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{a.sent_to || "—"}</td>
                  <td className="px-4 py-2">
                    <span title={a.status} className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${a.status === "sent" ? "bg-emerald-50 text-emerald-700" : a.status === "skipped" ? "bg-slate-100 text-slate-500" : "bg-red-50 text-red-700"}`}>{a.status === "sent" ? "sent" : a.status === "skipped" ? "skipped" : "error"}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{fmtDate(a.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
